import { useEffect, useState } from "react";
import { orderedStringify, getQs } from "src/utils";
import store from "src/store";
import api from "src/api";
import { addCdnImageToArticle, addSlugToObjects } from "../utils/aiFunctions";

const loadingState = (data = {}) => ({
  loading: true,
  data,
  error: undefined
});

const dataState = result => ({
  loading: false,
  data: result,
  error: undefined
});

const errorState = error => ({
  loading: false,
  data: {},
  error: error
});

const useAsyncState = ({ mapData = data => data }) => {
  const [value, setValue] = useState({ loading: false, data: {}, error: undefined });

  return {
    value,
    setLoading: () => setValue(loadingState(value.data)),
    setData: data => setValue(dataState(mapData(data, value.data))),
    setError: error => setValue(errorState(error))
  };
};

export const useAsync = (asyncFn, config = {}) => (...params) => {
  // callOnMount  - skip the initial async fn call when the component is mounted
  // callOnChange - call the async fn automatically every time the params change
  const { callOnMount = true, callOnChange = false, cache = false } = config;
  const asyncState = useAsyncState(config);
  const watching = callOnChange ? [orderedStringify(params)] : [];

  const fn = cache ? cachePromise(asyncFn) : asyncFn;

  const handleAsync = (...overrideParams) => {
    asyncState.setLoading();
    const p = overrideParams.length > 0 ? overrideParams : params;

    if (asyncFn.name === "getSearch" || asyncFn.name === "getSponsoredSearch") {
      store.set("apiURL", {
        URL: asyncFn.name === "getSearch" ? window.GLOBALS.API.SEARCH + getQs(p[0]) : window.GLOBALS.API.SPONSORED_SEARCH + getQs(p[0])
      });
    }

    return fn(...p)
      .then(result => {
        asyncState.setData(result);
        return result;
      })
      .catch(error => {
        asyncState.setError(error);
      });
  };

  // Only use this effect once (second arg is an empty array, so it won't be re-run on changes)
  // We could change this to the params vararg array, so each time the params change, we reuse the effect
  useEffect(() => {
    callOnMount && handleAsync();
  }, watching);

  return {
    ...asyncState.value,
    call: handleAsync
  };
};

const cache = {};
const times = {};

export const cachePromise = (fn, expire = 1000, hashFn = orderedStringify) => {
  cache[fn.name] = cache[fn.name] || {};
  times[fn.name] = times[fn.name] || {};

  return (...args) => {
    const hash = hashFn(args);
    const cached = cache[fn.name][hash];
    const expired = new Date() < times[fn.name][hash] + expire;

    if (!cached || (cached && expired)) {
      cache[fn.name][hash] = fn(...args); // eslint-disable-line
      times[fn.name][hash] = new Date(); // eslint-disable-line
    }

    return cache[fn.name][hash];
  };
};
/* 
  nrOFBlocks : number -> In how many blocks we will separate the articles
  nrOfArticlesPerBlock : number[] -> Sequence of numbers showing the number of articles you need for each block. 1st index -> 1st block ....
*/
export const useContentAIArticles = ({ totalNrOfArticles, nrOfBlocks, nrOfArticlesPerBlock, fullCategoryName, language, clientId }) => {
  let data = [];
  let loading = false;
  let error = false;

  let articleSeparation = [];

  const useContentAI = useAsync(api.getContentAiArticles);

  const aiArticles = useContentAI({
    category: encodeURIComponent(fullCategoryName || "/"),
    useCache: true,
    lang: language || "english",
    take: totalNrOfArticles,
    ignoreRandomFill: true,
    fill: true,
    orderStrategy: "newest",
    clientId
  });

  loading = aiArticles.loading;
  data = aiArticles.data;
  if (data && Array.isArray(data)) {
    data = addCdnImageToArticle(data);
    data = addSlugToObjects(data);
    let initialShift = 0;

    for (let i = 0; i < nrOfBlocks; i++) {
      articleSeparation.push(data.slice(initialShift, nrOfArticlesPerBlock[i] + initialShift));
      initialShift += nrOfArticlesPerBlock[i];
    }
  }
  error = aiArticles.error;

  return { loading, error, articleSeparation };
};
